/**
 * The chart adapter: semantic contract in, ECharts option out.
 *
 * This is the only file in the app that knows ECharts exists. The API, the
 * fixtures and the domain types stay library-agnostic, so replacing the
 * renderer means rewriting this file and nothing else.
 *
 * Two deliberate departures from generic chart advice, both driven by the
 * product this reproduces (see docs/design-contract.md):
 *
 *   1. The price and flow panels each carry two y-scales. Price and cumulative
 *      value flow, and proportion and net value, are read *against* each other
 *      by the analyst; splitting them into separate charts is what the desk
 *      already rejects about generic tooling. Each axis is labelled and each
 *      series is legended and direct-coloured.
 *   2. Up/down is green/red, which no deuteranopia check can pass. Every mark
 *      that uses it carries a second encoding: candle bodies are hollow up and
 *      filled down, and net-value bars sit above or below the zero baseline.
 */
import type { EChartsOption } from "echarts";

import type { Maybe, WebChart } from "../domain/chart.ts";
import { ink, market, series as seriesColor, surface, typography, withAlpha, zone } from "../design/tokens.ts";
import { formatMoney, formatMonth, formatPrice } from "../domain/format.ts";

export interface ChartLayout {
	/**
	 * Render the holding-composition column inside the chart.
	 *
	 * Off below the tablet breakpoint: at 375px the column would be a few
	 * pixels wide, so the same data is shown as a list in the evidence panel.
	 */
	showHolding: boolean;
	/** Drop axis names and shrink margins when vertical space is tight. */
	compact: boolean;
}

export const RATIO_TO_PERCENT = 100;

/** A ratio series as percent points, keeping nulls as gaps rather than zeroes. */
export function toPercentPoints(values: readonly Maybe[]): Maybe[] {
	return values.map((value) => (value === null ? null : value * RATIO_TO_PERCENT));
}

/**
 * ECharts candlestick wants [open, close, low, high] — not OHLC order.
 *
 * A bar missing any leg cannot be drawn, so it becomes a gap instead of a
 * partially-invented candle.
 */
export function toCandlestickData(price: WebChart["price"]): Array<Array<number> | null> {
	return price.dates.map((_, index) => {
		const open = price.open[index];
		const close = price.close[index];
		const low = price.low[index];
		const high = price.high[index];
		if (open === null || close === null || low === null || high === null) return null;
		if (open === undefined || close === undefined || low === undefined || high === undefined) return null;
		return [open, close, low, high];
	});
}

/** The net-value profile layer, named the same way the origin chart names it. */
export const PROFILE_NAME = "Net Value Profile";

/**
 * Width of the reserved profile strip, as a percentage of the canvas.
 *
 * The price and flow panels are inset by this much on top of their normal
 * right margin, so the strip is empty chart space rather than an overlay: the
 * histogram cannot cover a single candle.
 */
export const PROFILE_STRIP_PERCENT = { comfortable: 10, compact: 16 } as const;
/** Gap between the last candle and the first profile bar, in px. */
const PROFILE_GAP = 8;
/** Gap between stacked bars so the binning is legible, in px. */
const PROFILE_BIN_GAP = 1;

/** One row per bin: `[low, high, net_value, is_peak]`. Nothing is dropped. */
export function profileData(chart: WebChart): number[][] {
	return chart.profile_zones.map((entry) => [entry.low, entry.high, entry.net_value ?? 0, entry.is_peak ? 1 : 0]);
}

/** Longest bar in the strip; every other bar is a fraction of it. */
export function profileStrongest(chart: WebChart): number {
	return chart.profile_zones.reduce((peak, entry) => Math.max(peak, Math.abs(entry.net_value ?? 0)), 0);
}

/**
 * The net-value profile, as a real histogram in its own right-hand strip.
 *
 * Two earlier attempts were wrong in opposite directions: peak-only markArea
 * bands put one stray rectangle on the panel, and all-bins markArea tinted the
 * entire candle history red. A profile is a histogram — magnitude has to be a
 * length, and it has to live somewhere that is not on top of the price.
 *
 * So each bin is a rect spanning its own `low..high` on the shared price axis,
 * with length proportional to `|net_value|` against the strongest bin, growing
 * rightwards from the edge of the candles into reserved space. Sign picks the
 * market colour; peaks are near-opaque with a bright edge, ordinary bins are
 * half transparent.
 *
 * Bars are clamped to the visible price range, so zooming the price axis
 * crops the strip instead of letting it spill out of the panel.
 *
 * LIMITATION, deliberately not papered over: `profile_zones` carries a price
 * range and a net value per bin, and no time extent. The origin Plotly chart
 * draws this same histogram on an overlay axis across the panel, which reads
 * as a staircase over time — but that horizontal extent is magnitude, not a
 * date range. Reproducing it in place would mean inventing time boundaries the
 * contract does not carry, so the histogram gets its own strip instead. See
 * docs/chart-contract.md.
 */
function profileRenderItem(stripPercent: number, strongest: number) {
	return (params: CustomParams, api: CustomApi): CustomElement | undefined => {
		const grid = params.coordSys;
		const flow = api.value(2);
		const isPeak = api.value(3) === 1;

		const edgeA = api.coord([0, api.value(1)])[1] ?? 0;
		const edgeB = api.coord([0, api.value(0)])[1] ?? 0;
		const top = Math.max(Math.min(edgeA, edgeB), grid.y);
		const bottom = Math.min(Math.max(edgeA, edgeB), grid.y + grid.height);
		// Scrolled out of the visible price range: draw nothing rather than
		// smearing the bin against the edge of the panel.
		if (bottom <= top) return undefined;

		const strip = Math.max((api.getWidth() * stripPercent) / 100 - PROFILE_GAP, 8);
		const length = strongest > 0 ? Math.max((strip * Math.abs(flow)) / strongest, 1) : 0;
		if (length <= 0) return undefined;

		return {
			type: "rect",
			shape: {
				x: grid.x + grid.width + PROFILE_GAP,
				y: top,
				width: length,
				height: Math.max(bottom - top - PROFILE_BIN_GAP, 1),
			},
			style: {
				fill: withAlpha(flow < 0 ? market.down : market.up, isPeak ? zone.fill.peak : zone.fill.plain),
				stroke: isPeak ? zone.peakEdge : undefined,
				lineWidth: isPeak ? 1 : 0,
			},
		};
	};
}

interface CustomParams {
	coordSys: { x: number; y: number; width: number; height: number };
}

interface CustomApi {
	value(dimension: number): number;
	coord(point: number[]): number[];
	getWidth(): number;
}

interface CustomElement {
	type: "rect";
	shape: { x: number; y: number; width: number; height: number };
	style: { fill: string; stroke?: string; lineWidth: number };
}

/**
 * Zoom slider geometry.
 *
 * The slider sat 4px off the bottom edge, which on a phone is underneath the
 * browser's own chrome — draggable only by fighting the URL bar. Compact
 * layouts lift it clear of that band and give it a touch-sized track.
 */
export const DATA_ZOOM = {
	comfortable: { bottom: 10, height: 18 },
	compact: { bottom: 48, height: 28 },
} as const;

const axisLine = { lineStyle: { color: surface.border } };
const splitLine = { lineStyle: { color: surface.grid } };
const labelStyle = { color: ink.muted, fontFamily: typography.mono, fontSize: 11 };

export function buildChartOption(chart: WebChart, layout: ChartLayout): EChartsOption {
	const { abbrev } = chart.annotations;
	const holding = layout.showHolding ? chart.holding_composition : null;
	const holdingWidth = holding ? 15 : 0;
	const chartRight = holding ? `${holdingWidth + 8}%` : layout.compact ? "6%" : "3%";
	// The panels are inset by the strip width so the histogram has reserved
	// space to grow into. It never draws over a candle.
	const stripPercent = layout.compact ? PROFILE_STRIP_PERCENT.compact : PROFILE_STRIP_PERCENT.comfortable;
	const panelRight = `${Number.parseFloat(chartRight) + stripPercent}%`;
	const zoom = layout.compact ? DATA_ZOOM.compact : DATA_ZOOM.comfortable;
	// The legend wraps to two rows on a narrow screen; without the extra top
	// inset it prints straight over the first candles.
	const priceTop = layout.compact ? 52 : 24;
	// Clear of the y-axis name, which is printed at the grid's left edge.
	const legendLeft = layout.compact ? 8 : 120;

	const names = {
		vwap: `${abbrev}-VWAP`,
		valueFlow: `${abbrev} Value Flow`,
		proportion: `${abbrev} Proportion`,
		netProportion: `${abbrev} Net Proportion`,
		netValue: `${abbrev} Net Value`,
	};

	const holdingLabels = holding?.periods.map(formatMonth) ?? [];

	return {
		backgroundColor: surface.panel,
		animationDuration: 240,
		textStyle: { fontFamily: typography.sans, color: ink.secondary },
		// The flow panel starts just under the price panel: the two are read
		// together, and the 60% gap this replaced was dead space. Not zero,
		// though — at zero the price axis's bottom label and the proportion
		// axis's top label print on top of each other once the panel is short.
		// Compact panels still stop at 78% so the x-axis labels and the lifted
		// slider below them have room that does not overlap either.
		grid: [
			{ left: 62, right: panelRight, top: priceTop, height: layout.compact ? "40%" : "46%" },
			{ left: 62, right: panelRight, top: "55%", height: layout.compact ? "23%" : "25%" },
			...(holding ? [{ right: "2%", width: `${holdingWidth}%`, top: priceTop, height: layout.compact ? "40%" : "48%" }] : []),
		],
		axisPointer: { link: [{ xAxisIndex: [0, 1] }], label: { backgroundColor: surface.raised, color: ink.primary } },
		tooltip: {
			trigger: "axis",
			backgroundColor: surface.raised,
			borderColor: surface.border,
			textStyle: { color: ink.primary, fontFamily: typography.mono, fontSize: 12 },
			axisPointer: { type: "cross", crossStyle: { color: ink.muted }, lineStyle: { color: ink.muted } },
			formatter: (params) => tooltipHtml(params as unknown as TooltipParam[], names),
		},
		legend: [
			{
				top: 0,
				left: legendLeft,
				itemGap: layout.compact ? 10 : 16,
				itemWidth: 14,
				itemHeight: 3,
				icon: "roundRect",
				textStyle: { color: ink.secondary, fontSize: layout.compact ? 10 : 11 },
				inactiveColor: surface.border,
				data: [names.vwap, names.valueFlow, names.proportion, names.netProportion, names.netValue, PROFILE_NAME],
			},
			...(holding
				? [{
					bottom: 0,
					right: "2%",
					itemGap: 10,
					itemWidth: 10,
					itemHeight: 10,
					icon: "roundRect",
					orient: "vertical" as const,
					textStyle: { color: ink.secondary, fontSize: 10 },
					inactiveColor: surface.border,
					data: ["Foreign", "Local institutional", "Local individual"],
				}]
				: []),
		],
		xAxis: [
			{ type: "category", data: chart.price.dates, gridIndex: 0, boundaryGap: true, axisLine, axisTick: { show: false }, axisLabel: { show: false }, splitLine: { show: false }, axisPointer: { label: { formatter: (value) => String(value.value) } } },
			{ type: "category", data: chart.price.dates, gridIndex: 1, boundaryGap: true, axisLine, axisTick: { show: false }, axisLabel: { ...labelStyle, hideOverlap: true }, splitLine: { show: false } },
			...(holding ? [{ type: "category" as const, data: holdingLabels, gridIndex: 2, axisLine, axisTick: { show: false }, axisLabel: { ...labelStyle, fontSize: 9, interval: 1, rotate: 90 }, splitLine: { show: false } }] : []),
		],
		yAxis: [
			{
				type: "value",
				gridIndex: 0,
				scale: true,
				name: layout.compact ? "" : "Price",
				nameTextStyle: { color: ink.muted, fontSize: 10, align: "left" },
				axisLine: { show: false },
				axisLabel: { ...labelStyle, formatter: (value: number) => formatPrice(value) },
				splitLine,
			},
			{
				type: "value",
				gridIndex: 0,
				position: "right",
				scale: true,
				axisLine: { show: false },
				axisLabel: { show: !holding && !layout.compact, ...labelStyle, formatter: (value: number) => formatMoney(value, 1) },
				splitLine: { show: false },
			},
			{
				type: "value",
				gridIndex: 1,
				min: 0,
				max: 100,
				name: layout.compact ? "" : `${abbrev} Prop %`,
				nameTextStyle: { color: ink.muted, fontSize: 10, align: "left" },
				axisLine: { show: false },
				axisLabel: { ...labelStyle, formatter: (value: number) => `${value}%` },
				splitLine,
			},
			{
				type: "value",
				gridIndex: 1,
				position: "right",
				scale: true,
				axisLine: { show: false },
				axisLabel: { show: !holding && !layout.compact, ...labelStyle, formatter: (value: number) => formatMoney(value, 1) },
				splitLine: { show: false },
			},
			...(holding
				? [{
					type: "value" as const,
					gridIndex: 2,
					min: 0,
					max: 100,
					position: "right" as const,
					axisLine: { show: false },
					axisLabel: { ...labelStyle, fontSize: 9, formatter: (value: number) => `${value}%` },
					splitLine,
				}]
				: []),
		],
		dataZoom: [
			{ type: "inside", xAxisIndex: [0, 1], start: 55, end: 100, zoomOnMouseWheel: "shift" },
			{
				type: "slider",
				xAxisIndex: [0, 1],
				start: 55,
				end: 100,
				bottom: zoom.bottom,
				height: zoom.height,
				// A 9px handle is a miss on a touch screen; widen it when compact.
				handleSize: layout.compact ? "120%" : "100%",
				moveHandleSize: layout.compact ? 10 : 7,
				left: 62,
				right: panelRight,
				backgroundColor: surface.raised,
				borderColor: surface.border,
				fillerColor: "rgba(164, 107, 242, 0.14)",
				handleStyle: { color: seriesColor.vwap },
				dataBackground: { lineStyle: { color: surface.border }, areaStyle: { color: surface.hover } },
				selectedDataBackground: { lineStyle: { color: seriesColor.vwap }, areaStyle: { color: "rgba(164, 107, 242, 0.10)" } },
				textStyle: { color: ink.muted, fontSize: 10 },
			},
		],
		series: [
			{
				type: "candlestick",
				name: "Price",
				data: toCandlestickData(chart.price),
				xAxisIndex: 0,
				yAxisIndex: 0,
				barMaxWidth: 9,
				// Hollow up / filled down: the sign is readable without colour.
				itemStyle: { color: "transparent", color0: market.down, borderColor: market.up, borderColor0: market.down, borderWidth: 1.2 },
				z: 3,
			},
			{
				type: "custom",
				name: PROFILE_NAME,
				data: profileData(chart),
				xAxisIndex: 0,
				yAxisIndex: 0,
				// Bars are drawn beside the grid, not inside it, so clipping has
				// to be off; renderItem clamps them to the panel itself.
				clip: false,
				// Behind every mark that reads on top of the price.
				z: 1,
				// ECharts only runs renderItem for data it can place, so dimension 0
				// has to be encoded to x even though the bar's position comes from
				// coordSys, not from the date axis. Without this, zero bars render.
				encode: { x: 0, y: [0, 1] },
				renderItem: profileRenderItem(stripPercent, profileStrongest(chart)),
			},
			{
				type: "line",
				name: names.vwap,
				data: chart.indicators.vwap,
				xAxisIndex: 0,
				yAxisIndex: 0,
				showSymbol: false,
				connectNulls: false,
				lineStyle: { width: 2, color: seriesColor.vwap },
				itemStyle: { color: seriesColor.vwap },
				z: 4,
			},
			{
				type: "line",
				name: names.valueFlow,
				data: chart.indicators.value_flow,
				xAxisIndex: 0,
				yAxisIndex: 1,
				showSymbol: false,
				connectNulls: false,
				lineStyle: { width: 2, color: seriesColor.valueFlow },
				itemStyle: { color: seriesColor.valueFlow },
				z: 3,
			},
			{
				type: "line",
				name: names.proportion,
				data: toPercentPoints(chart.indicators.proportion),
				xAxisIndex: 1,
				yAxisIndex: 2,
				showSymbol: false,
				connectNulls: false,
				lineStyle: { width: 2, color: seriesColor.proportion },
				itemStyle: { color: seriesColor.proportion },
				z: 4,
			},
			{
				type: "line",
				name: names.netProportion,
				data: toPercentPoints(chart.indicators.net_proportion),
				xAxisIndex: 1,
				yAxisIndex: 2,
				showSymbol: false,
				connectNulls: false,
				lineStyle: { width: 2, color: seriesColor.netProportion },
				itemStyle: { color: seriesColor.netProportion },
				z: 4,
			},
			{
				type: "bar",
				name: names.netValue,
				data: chart.indicators.net_value.map((value) => ({
					value,
					itemStyle: { color: (value ?? 0) < 0 ? market.down : market.up },
				})),
				xAxisIndex: 1,
				yAxisIndex: 3,
				barMaxWidth: 9,
				z: 2,
			},
			...(holding
				? ([
					["Foreign", holding.foreign, seriesColor.foreign],
					["Local institutional", holding.local_institutional, seriesColor.localInstitutional],
					["Local individual", holding.local_individual, seriesColor.localIndividual],
				] as const).map(([name, values, color]) => ({
					type: "bar" as const,
					name,
					stack: "holding",
					data: toPercentPoints(values),
					xAxisIndex: 2,
					yAxisIndex: 4,
					// 2px surface gap between stacked segments.
					itemStyle: { color, borderColor: surface.panel, borderWidth: 1 },
				}))
				: []),
		] as EChartsOption["series"],
	};
}

interface TooltipParam {
	seriesName: string;
	axisValue: string;
	value: unknown;
	marker: string;
}

function tooltipHtml(params: TooltipParam[], names: Record<string, string>): string {
	if (!params.length) return "";
	const rows = params
		.map((param) => `<div>${param.marker} ${escapeHtml(param.seriesName)} <b>${tooltipValue(param, names)}</b></div>`)
		.join("");
	return `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(params[0]?.axisValue ?? "")}</div>${rows}`;
}

function tooltipValue(param: TooltipParam, names: Record<string, string>): string {
	const raw = Array.isArray(param.value) ? param.value : (param.value as { value?: unknown })?.value ?? param.value;

	if (param.seriesName === "Price") {
		const candle = raw as Array<number> | null;
		if (!Array.isArray(candle)) return "—";
		// ECharts prepends the data index to candlestick values.
		const [, open, close, low, high] = candle;
		return `O ${formatPrice(open)} H ${formatPrice(high)} L ${formatPrice(low)} C ${formatPrice(close)}`;
	}
	if (typeof raw !== "number") return "—";
	if (param.seriesName === names.proportion || param.seriesName === names.netProportion) return `${raw.toFixed(2)}%`;
	if (param.seriesName === names.vwap) return formatPrice(raw, 2);
	if (param.seriesName.startsWith("Foreign") || param.seriesName.startsWith("Local")) return `${raw.toFixed(2)}%`;
	return formatMoney(raw);
}

function escapeHtml(value: string): string {
	return value.replace(/[&<>"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character] ?? character);
}
